import loadable from "@loadable/component";
import classnames from "classnames";
import React, { useMemo } from "react";
import { blockMap } from "../../../../core/content-blocks";
import { BlockBase } from "../../../../core/decoders/ContentBlocks.types";
import type { BlockFragment, PublishRecordFragment } from "../../../../core/generated/webapi.g";
import { logger } from "../../../../core/lib/logging";
import { Content } from "../../../../core/page-blocks/content/Content.component";
import { getCoastHints } from "../../../../core/services/Sites.utils";
import { COAST_CONTENT_TYPES } from "../../lib/coastCommon";
import "./CoastContent.style.scss";

const CoastContentGuest = loadable(() => import("./CoastContentGuest.component"));
const CoastContentShow = loadable(() => import("./CoastContentShow.component"));
const CoastHostDetail = loadable(() => import("./CoastHostDetail.component"));
const CoastContentArticle = loadable(() => import("./CoastContentArticle.component"));

export type CoastContentProps = {
    content: PublishRecordFragment;
    block: BlockFragment;
};

export const CoastContent = ({ content, block }: CoastContentProps) => {
    const hints = useMemo(() => getCoastHints(block.tags), [block.tags]);

    switch (content.type) {
        case COAST_CONTENT_TYPES.show:
            return <CoastContentShow content={content} />;
        case COAST_CONTENT_TYPES.guest:
            return <CoastContentGuest content={content} />;
        case COAST_CONTENT_TYPES.article:
            if (hints.includes("coast-host-detail")) {
                return <CoastHostDetail content={content} />;
            }
            return <CoastContentArticle content={content} coastHints={hints} />;
        default:
            return <Content content={content} block={block} />;
    }
};

export function isBlockScheduled({ schedule }: BlockBase, now = Date.now()) {
    if (!schedule || !schedule.enabled) {
        return true;
    }

    const begin = schedule.begin || now;
    const end = schedule.end || Number.MAX_SAFE_INTEGER;

    return begin <= now && end > now;
}

export const CoastContentBlock = ({ block }: { block: BlockBase }) => {
    const log = logger.getChild("Coast Content Block");

    if (!block) {
        log.error("No Block given to CoastContentBlock");
    }

    if (!isBlockScheduled(block) || !blockMap[block.type]) {
        return null;
    }

    try {
        const el = React.createElement<BlockBase>(blockMap[block.type], block);

        const blockClass = classnames("content-block", { [`type-${block.type}`]: block.type });

        return <section className={blockClass}>{el}</section>;
    } catch (e) {
        log.debug(e.message);
    }

    return null;
};
